export const productMenuKeys = [
    "workbench",
    "project-management",
    "annotation-configuration",
    "model-management",
    "config-management",
    "training-center",
    "team-members",
];
export const productFeatureKeys = [
    "workbench",
    "projectManagement",
    "annotationConfiguration",
    "teamMembers",
    "operationConsole",
];
function assertExactKeys(value, allowedKeys, path) {
    const allowed = new Set(allowedKeys);
    for (const key of Object.keys(value)) {
        if (!allowed.has(key))
            throw new TypeError(`${path}.${key} is not allowed`);
    }
}
function assertText(value, path) {
    if (typeof value !== "string" || !value.trim())
        throw new TypeError(`${path} must be a non-empty string`);
}
function assertNoTenantKeys(value, path) {
    if (!value || typeof value !== "object")
        return;
    for (const [key, child] of Object.entries(value)) {
        if (/^tenant/i.test(key))
            throw new TypeError(`${path}.${key} is not allowed`);
        assertNoTenantKeys(child, `${path}.${key}`);
    }
}
function deepFreeze(value) {
    if (value && typeof value === "object" && !Object.isFrozen(value)) {
        for (const child of Object.values(value))
            deepFreeze(child);
        Object.freeze(value);
    }
    return value;
}
export function defineProductConfig(config) {
    assertExactKeys(config, ["code", "brand", "navigation", "features", "api"], "product");
    assertExactKeys(config.brand, [
        "applicationName",
        "platformName",
        "shortName",
        "accessibleName",
        "logoPath",
        "loginEyebrow",
        "loginTitle",
        "loginHeading",
        "loginDescription",
        "loginHighlights",
        "loginPartnerMarks",
        "loginBackgroundImage",
        "copyright",
    ], "product.brand");
    assertExactKeys(config.navigation, ["menuKeys"], "product.navigation");
    assertExactKeys(config.features, productFeatureKeys, "product.features");
    assertExactKeys(config.api, ["basePath"], "product.api");
    assertNoTenantKeys(config, "product");
    assertText(config.brand.applicationName, "product.brand.applicationName");
    assertText(config.brand.platformName, "product.brand.platformName");
    assertText(config.brand.shortName, "product.brand.shortName");
    assertText(config.brand.accessibleName, "product.brand.accessibleName");
    assertText(config.brand.logoPath, "product.brand.logoPath");
    assertText(config.brand.loginEyebrow, "product.brand.loginEyebrow");
    assertText(config.brand.copyright, "product.brand.copyright");
    assertText(config.brand.loginTitle, "product.brand.loginTitle");
    assertText(config.brand.loginHeading, "product.brand.loginHeading");
    assertText(config.brand.loginDescription, "product.brand.loginDescription");
    if (!Array.isArray(config.brand.loginHighlights) || !Array.isArray(config.brand.loginPartnerMarks))
        throw new TypeError("loginHighlights and loginPartnerMarks must be arrays");
    config.brand.loginHighlights.forEach((text, index) => assertText(text, `product.brand.loginHighlights[${index}]`));
    config.brand.loginPartnerMarks.forEach((mark, index) => {
        const path = `product.brand.loginPartnerMarks[${index}]`;
        assertExactKeys(mark, ["src", "alt", "tone"], path);
        assertText(mark.src, `${path}.src`);
        assertText(mark.alt, `${path}.alt`);
        if (mark.tone !== "original" && mark.tone !== "muted")
            throw new TypeError(`${path}.tone must be original or muted`);
    });
    if (config.brand.loginBackgroundImage !== undefined)
        assertText(config.brand.loginBackgroundImage, "product.brand.loginBackgroundImage");
    assertText(config.api.basePath, "product.api.basePath");
    if (!config.api.basePath.startsWith("/"))
        throw new TypeError("product.api.basePath must start with /");
    const menuKeys = new Set(config.navigation.menuKeys);
    if (menuKeys.size !== config.navigation.menuKeys.length)
        throw new TypeError("product.navigation.menuKeys must not contain duplicates");
    for (const menuKey of menuKeys) {
        if (!productMenuKeys.includes(menuKey))
            throw new TypeError("product.navigation.menuKeys contains an unsupported key");
    }
    return deepFreeze(config);
}
//# sourceMappingURL=product-config.js.map